<template>
  <v-container fluid grid-list-md class="pa-0 ma-0">
    <SubBatteryDialog v-model="isSubBattery" :sub-battery-list="filteredSubBatteryList" :m2m-fe-map="m2mFeMap"
      :active-main-battery-id="activeMainBatteryId" :active-main-battery-name="activeMainBatteryName" />

    <v-layout row wrap>
      <v-flex xs12 md6 lg5 class="pt-0 ma-0">
        <SoCCard :main-battery-list="mainBatteryList" style="width: 100%" />
      </v-flex>

      <v-flex xs12 md12 lg5 class="pt-0 ma-0">
        <PowerFlowCard />
      </v-flex>

      <v-flex xs12 md6 lg2 class="pt-0 ma-0">
        <SettingsCard :content="emcData" style="width: 100%" />
      </v-flex>

      <v-flex xs12 lg6>
        <MainBatteriesCard :main-battery-list="mainBatteryList" @show-sub-battery="showSubBatteryHandler" />
      </v-flex>

      <v-flex xs12 lg6>
        <LiveMonitoringCard :series="series" :x-axis-data="xAxisData" :y-axis="yAxis" :theme="theme" />
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { mixin, mapGettersMixin } from "@/util/mixin/index";
import BatteryService from "@/services/Battery";

import SubBatteryDialog from "@/components/Battery/SubBatteryDialog.vue";
import SoCCard from "@/components/Battery/SoCCard.vue";
import PowerFlowCard from "@/components/Battery/PowerFlowCard.vue";
import SettingsCard from "@/components/Battery/SettingsCard.vue";
import MainBatteriesCard from "@/components/Battery/MainBatteriesCard.vue";
import LiveMonitoringCard from "@/components/Battery/LiveMonitoringCard.vue";

const moment = require("moment");
const momentTz = require("moment-timezone");

export default {
  mixins: [mixin, mapGettersMixin],
  components: {
    SubBatteryDialog,
    SoCCard,
    PowerFlowCard,
    SettingsCard,
    MainBatteriesCard,
    LiveMonitoringCard,
  },
  data() {
    return {
      m2mFeMap: {
        balance_status: { name: "Balance Status", unit: "" },
        chrg_dischrg_curnt: { name: "Chr/Dschr Curr", unit: "A" },
        dc_out_vlt: { name: "DC Outside Volt", unit: "V" },
        demand_pwr: { name: "TMS Demand Pwr", unit: "kW" },
        env_temp: { name: "TMS Env Temp", unit: "°C" },
        fan_status: { name: "Fan Status", unit: "" },
        fault_code: { name: "TMS Fault Code", unit: "" },
        master_stat_negtv: { name: "Mastr Stat Negtv Rel", unit: "" },
        master_stat_postv: { name: "Mastr Stat Postv Rel", unit: "" },
        mode_cmd: { name: "TMS Mode Cmd", unit: "" },
        pre_chrg_stat: { name: "Pre-Chrg Relay", unit: "" },
        rack_inlet_temp: { name: "Rack Inlet Temp", unit: "°C" },
        rack_outlet_temp: { name: "Rack Outlet Temp", unit: "°C" },
        real_mode: { name: "TMS Real Mode", unit: "" },
        soc: { name: "SoC", unit: "%" },
        soh: { name: "SoH", unit: "%" },
        temp_set_val: { name: "TMW Temp Set Val", unit: "°C" },
        total_cell_vlt: { name: "SBMU Tot Cell Volt", unit: "V" },
        warning_0: { name: "SBMU Warning 0", unit: "" },
        warning_1: { name: "SBMU Warning 1", unit: "" },
        warning_2: { name: "SBMU Warning 2", unit: "" },
        max_chrg_cur_alwd: { name: "Max Chrg Curr Allwd", unit: "A" },
        avg_cell_vlt: { name: "Avg Cell Volt", unit: "v" },
        avg_cell_temp: { name: "Avg Cell Temp", unit: "°C" },
      },
      isSubBattery: false,
      showSubBatteryDetails: true,
      mainBatteryList: {},
      emcData: {},
      subBatteryList: {},
      activeMainBatteryId: null,
      activeSubBatteryId: null,
      series: [],
      xAxisData: [],
      yAxis: [{ type: "value" }],
    };
  },
  async mounted() {
    const mainBattResponse = await BatteryService.getMainBatteryData(this.getSelectedPlantId);
    this.mainBatteryList = mainBattResponse.data.message;
    if (Object.keys(this.mainBatteryList).length > 0) {
      this.activeMainBatteryId = Object.keys(this.mainBatteryList)[0];
      console.log(this.activeMainBatteryId, 'activeMainBatteryId', this.mainBatteryList);
    }

    const emcResponse = await BatteryService.getEmcData(this.getSelectedPlantId);
    this.emcData = emcResponse.data.message ? Object.values(emcResponse.data.message)[0] : {};
    
    const subBattResponse = await BatteryService.getSubBatteryData(this.getSelectedPlantId);
    for (const [key, value] of Object.entries(subBattResponse.data.message)) {
      const { soc, soh, name } = value;

      const tempObj = {
        name,
        soc,
        soh,
        dod: soc === null ? null : 100 - soc,
        details: {},
        legend: [],
        imageRep: {},
      };
      Object.keys(value).forEach((param) => {
        if (
          !["soc", "soh", "max_chrg_cur_alwd", "fan_status", "avg_cell_vlt", "avg_cell_temp", "balance_status", "master_stat_negtv", "master_stat_postv", "pre_chrg_stat", "dod"].includes(param)
        ) {
          tempObj.details[param] = value[param];
        }

        if (
          ["soc", "soh", "max_chrg_cur_alwd", "fan_status", "avg_cell_vlt", "avg_cell_temp"].includes(param)
        ) {
          tempObj.imageRep[param] = value[param];
        }
        if (
          ["balance_status", "master_stat_negtv", "master_stat_postv", "pre_chrg_stat"].includes(param)
        ) {
          tempObj.legend.push({
            name: this.m2mFeMap[param].name,
            attr: {
              name: "square",
              color: value[param] == null ? "#9e9e9e" : (value[param] == 0 ? "#FF5252" : "#3CB371"),
            },
          });
        }
      });

      this.$set(this.subBatteryList, key, tempObj);
    }

    this.$mqtt.subscribe(`live/${this.getSelectedPlantId}/main battery`);
    this.$mqtt.on("message", (topic, message) => {
      if (topic.startsWith("live/")) {
        if (topic.endsWith("main battery")) {
          const mqttData = JSON.parse(message.toString()).deviceList[0];
          if (!mqttData.exception) {
            const deviceId = mqttData.deviceId;
            if (this.mainBatteryList[deviceId]) {
              this.$set(this.mainBatteryList[deviceId], 'soc', mqttData.soc);
              this.$set(this.mainBatteryList[deviceId], 'soh', mqttData.soh);
              this.$set(this.mainBatteryList[deviceId], 'chrg_dischrg_curnt', mqttData.chrg_dischrg_curnt);
              this.$set(this.mainBatteryList[deviceId], 'subsys_cnt', mqttData.subsys_cnt);
              this.$set(this.mainBatteryList[deviceId], 'max_cell_temp', mqttData.max_cell_temp);
              this.$set(this.mainBatteryList[deviceId], 'min_cell_temp', mqttData.min_cell_temp);
              this.$set(this.mainBatteryList[deviceId], 'max_cell_vlt', mqttData.max_cell_vlt);
              this.$set(this.mainBatteryList[deviceId], 'min_cell_vlt', mqttData.min_cell_vlt);
              this.$set(this.mainBatteryList[deviceId], 'max_dischrg_cur_alwd', mqttData.max_dischrg_cur_alwd);
              this.$set(this.mainBatteryList[deviceId], 'max_chrg_cur_alwd', mqttData.max_chrg_cur_alwd);
            }

            if (deviceId === this.activeMainBatteryId) {
              const currentTime = momentTz(mqttData.sensor_time, "X")
                .tz(this.getActivePlantInfo("timezone"))
                .format('HH:mm');
              let totalPackets = this.xAxisData.length - 1
              let lastTime = momentTz(this.xAxisData[totalPackets], 'HH:mm').add(1, 'minutes').format('HH:mm')

              while (moment(currentTime, 'HH:mm').isSameOrAfter(moment(lastTime, 'HH:mm'))) {
                totalPackets += 1;
                let soc, soh;
                if (moment(currentTime, 'HH:mm').isSame(moment(lastTime, 'HH:mm'))) {
                  soc = mqttData.soc;
                  soh = mqttData.soh;
                } else {
                  soc = null;
                  soh = null;
                }
                this.$set(this.series[0].data, totalPackets, soc);
                this.$set(this.series[1].data, totalPackets, soh);
                this.$set(this.xAxisData, totalPackets, moment(lastTime, 'HH:mm').format('HH:mm'));
                lastTime = moment(lastTime, 'HH:mm').add(1, 'minutes').format('HH:mm')
              }
            }
          }
        }
      }
    });

    this.$mqtt.subscribe(`live/${this.getSelectedPlantId}/sub battery`);
    this.$mqtt.on("message", (topic, message) => {
      if (topic.startsWith("live/")) {
        if (topic.endsWith("sub battery")) {
          const mqttData = JSON.parse(message.toString()).deviceList[0];
          if (!mqttData.exception) {
            const { soc, soh, deviceId } = mqttData;
            const legendData = [];

            if (this.subBatteryList[deviceId]) {
              this.$set(this.subBatteryList[deviceId], "soc", +soc);
              this.$set(this.subBatteryList[deviceId], "soh", +soh);
              this.$set(this.subBatteryList[deviceId], "dod", 100 - +soc);

              Object.keys(mqttData).forEach((param) => {
                if (
                  !["soc", "soh", "max_chrg_cur_alwd", "fan_status", "avg_cell_vlt", "avg_cell_temp", "balance_status", "master_stat_negtv", "master_stat_postv", "pre_chrg_stat", "dod"].includes(param)
                ) {
                  this.$set(this.subBatteryList[deviceId].details, param, +mqttData[param]);
                }

                if (
                  ["soc", "soh", "max_chrg_cur_alwd", "fan_status", "avg_cell_vlt", "avg_cell_temp"].includes(param)
                ) {
                  this.$set(this.subBatteryList[deviceId].imageRep, param, +mqttData[param]);
                }
                if (
                  ["balance_status", "master_stat_negtv", "master_stat_postv", "pre_chrg_stat"].includes(param)
                ) {
                  legendData.push({
                    name: this.m2mFeMap[param].name,
                    attr: {
                      name: "square",
                      color: mqttData[param] == null ? "#9e9e9e" : (mqttData[param] == 0 ? "#FF5252" : "#3CB371"),
                    },
                  });
                }
              });

              this.$set(this.subBatteryList[deviceId], "legend", legendData);
            }
          }
        }
      }
    });

    await this.fetchMainBatteryGraph();
  },
  watch: {
    activeMainBatteryId(newVal) {
      if (newVal) {
        this.fetchMainBatteryGraph();
      }
    }
  },
  computed: {
    ...mapGetters([
      "getSelectedPlantId",
      "getGeneralState",
      "getActivePlantInfo",
    ]),
    filteredSubBatteryList() {
      if (!this.activeMainBatteryId) return {};
      const filtered = {};
      for (const [key, value] of Object.entries(this.subBatteryList)) {
        if (key.startsWith(this.activeMainBatteryId)) {
          filtered[key] = value;
        }
      }
      return filtered;
    },
    activeMainBatteryName() {
      if (!this.activeMainBatteryId || !this.mainBatteryList[this.activeMainBatteryId]) return null;
      return this.mainBatteryList[this.activeMainBatteryId].name;
    },
  },
  methods: {
    async fetchMainBatteryGraph() {
      const graphResponse = await BatteryService.fetchGraphData(this.getSelectedPlantId);
      
      const timeMap = new Map();
      const batteryNames = new Set(); 
      const nameToId = new Map();

      graphResponse.data.message
        .sort((a, b) => a.sensor_time - b.sensor_time)
        .forEach((el) => {
          const time = momentTz(el.sensor_time, "X")
            .tz(this.getActivePlantInfo("timezone"))
            .format("HH:mm");
          
          if (!timeMap.has(time)) {
            timeMap.set(time, {});
          }
          
          const name = el.name || el.id;
          batteryNames.add(name);
          nameToId.set(name, el.id);
          
          timeMap.get(time)[name] = { soc: el.soc, soh: el.soh };
        });

      const xAxis = Array.from(timeMap.keys());
      const sortedBatteryNames = Array.from(batteryNames).sort();
      
      const series = [];
      const colors = [
          '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'
      ];

      sortedBatteryNames.forEach((name, index) => {
        series.push({
          name: `${name} SoC (%)`,
          type: "line",
          smooth: true,
          showSymbol: false,
          data: [],
        });
        series.push({
          name: `${name} SoH (%)`,
          type: "line", 
          smooth: true,
          showSymbol: false,
           lineStyle: { type: 'dashed' },
          data: [],
        });
      });

      xAxis.forEach(time => {
        const timeData = timeMap.get(time);
        sortedBatteryNames.forEach((name, idx) => {
          const batteryData = timeData[name];
          series[idx * 2].data.push(batteryData ? batteryData.soc : null);
          series[idx * 2 + 1].data.push(batteryData ? batteryData.soh : null);
        });
      });

      this.xAxisData = xAxis;
      this.series = series;

      this.yAxis = [
        {
          name: "SoC / SoH (%)",
          axisLabel: { formatter: "{value}", rotate: 40 },
        },
      ];
    },
    showSubBatteryHandler(id) {
      this.isSubBattery = true;
      this.activeMainBatteryId = id;
    }
  },
};
</script>